import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Check, BookOpen, Shield, Users, Lightbulb, TrendingUp, Gift, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { TestimonialCard } from './components/TestimonialCard';
import { SocialProofPopup } from './components/SocialProofPopup';
function useInView(options = {}) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsInView(true);
    }, {
      threshold: 0.1,
      ...options
    });
    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);
  return [ref, isInView] as const;
}
export function App() {
  return <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white font-sans text-gray-800" data-id="element-0">
      <SocialProofPopup data-id="element-1" />

      {/* HERO */}
      <section className="relative flex items-center justify-center px-4 py-12 md:py-16 overflow-hidden" data-id="element-2">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/pzs7VPxJ16yFxyjVyuMdjY/Firefly_Gemini_Flash-12.png)',
        filter: 'blur(4px)',
        transform: 'scale(1.08)'
      }} data-id="element-3" />
        <div className="absolute inset-0 bg-blue-950/65" data-id="element-4" />

        <div className="relative z-10 max-w-5xl mx-auto text-center" data-id="element-5">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold mb-8 border border-white/25" data-id="element-6">
            <span data-id="element-7">📚</span>
            <span data-id="element-8">PARA DOCENTES DE NIVEL SECUNDARIO</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" data-id="element-9">
            <span className="text-white" data-id="element-10">ORGANIZA TODO TU AÑO DE </span>
            <span className="text-blue-300" data-id="element-11">ECONOMÍA SIN ESTRÉS</span>
          </h1>

          <div className="my-12 flex justify-center" data-id="element-12">
            <img src="https://cdn.magicpatterns.com/uploads/5BpCQQ8Jiju8J6W3NYcLKg/Cover_micro_y_macro.png" alt="Micro y Macro Economía 360" className="w-full max-w-lg drop-shadow-2xl hover:scale-105 transition-transform duration-500" data-id="element-13" />
          </div>

          <h2 className="text-2xl md:text-3xl font-black text-white mb-4 tracking-tighter" data-id="element-14">
            MICRO Y MACRO ECONOMÍA 360
          </h2>

          <p className="text-lg md:text-xl text-blue-100 mb-2 max-w-3xl mx-auto font-semibold" data-id="element-15">
            Sistema completo de actividades, proyectos y evaluaciones listos
            para usar.
          </p>
          <p className="text-base md:text-lg text-white/75 mb-8 max-w-3xl mx-auto" data-id="element-16">
            Todo el año de Micro y Macro economía resuelto sin planificar nada
            desde cero.
          </p>

          <div className="mb-6" data-id="element-17">
            <button onClick={() => document.getElementById('comprar')?.scrollIntoView({
            behavior: 'smooth'
          })} className="bg-blue-500 hover:bg-blue-400 text-white font-bold text-lg px-12 py-4 rounded-full shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300" data-id="element-18">
              QUIERO ACCESO INMEDIATO
              <div className="text-xs font-normal mt-1 opacity-90" data-id="element-19">
                Pago 100% seguro y encriptado
              </div>
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-white/80" data-id="element-20">
            <div className="flex items-center gap-2" data-id="element-21">
              <Check className="w-5 h-5 text-green-400" data-id="element-22" />
              <span data-id="element-23">Descarga inmediata</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-24">
              <Check className="w-5 h-5 text-green-400" data-id="element-25" />
              <span data-id="element-26">Garantía 7 días</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-27">
              <Check className="w-5 h-5 text-green-400" data-id="element-28" />
              <span data-id="element-29">Acceso de por vida</span>
            </div>
          </div>
        </div>
      </section>

      {/* 3 PILLARS - Compact */}
      <section className="py-8 px-4 bg-blue-900 text-white" data-id="element-30">
        <div className="max-w-4xl mx-auto" data-id="element-31">
          <div className="grid md:grid-cols-3 gap-3" data-id="element-32">
            {[{
            icon: <Lightbulb className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-33" />,
            title: 'Pedagogía Aplicada',
            desc: 'Transforma la teoría del libro en realidad del aula.'
          }, {
            icon: <Shield className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-34" />,
            title: 'Seguridad Total',
            desc: 'Llegás con todo listo. Sabés exactamente qué explicar.'
          }, {
            icon: <Users className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-35" />,
            title: 'Conexión Real',
            desc: 'Economía académica en el lenguaje del alumno.'
          }].map((item, i) => <div key={i} className="flex items-start gap-3 bg-blue-800/60 p-4 rounded-xl border border-blue-700" data-id="element-36">
                {item.icon}
                <div data-id="element-37">
                  <h3 className="font-bold text-sm mb-0.5" data-id="element-38">{item.title}</h3>
                  <p className="text-blue-200 text-xs leading-snug" data-id="element-39">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-16 px-4 bg-white" data-id="element-40">
        <div className="max-w-5xl mx-auto" data-id="element-41">
          <h2 className="text-3xl font-bold text-center text-blue-900 mb-12" data-id="element-42">
            ¿Qué incluye este pack?
          </h2>

          <div className="grid sm:grid-cols-2 gap-4 mb-16" data-id="element-43">
            {[{
            title: 'Clases estructuradas',
            desc: 'Listas para aplicar en el aula sin improvisar.'
          }, {
            title: 'Secuencia lógica y progresiva',
            desc: 'Un orden que tiene sentido para el alumno.'
          }, {
            title: 'Micro y Macro integradas',
            desc: 'Todo el contenido curricular cubierto.'
          }, {
            title: 'Ejemplos reales y actuales',
            desc: 'Casos que los chicos ven en su día a día.'
          }, {
            title: 'Lenguaje claro y didáctico',
            desc: 'Explicaciones que "bajan" la teoría a tierra.'
          }, {
            title: 'Material listo para usar',
            desc: 'Sin armar desde cero. Sin perder horas.'
          }].map((item, i) => <div key={i} className="flex gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100" data-id="element-44">
                <div className="bg-green-100 p-1.5 rounded-full h-fit flex-shrink-0" data-id="element-45">
                  <Check className="w-4 h-4 text-green-600" data-id="element-46" />
                </div>
                <div data-id="element-47">
                  <h4 className="font-bold text-gray-900 text-sm" data-id="element-48">
                    {item.title}
                  </h4>
                  <p className="text-gray-600 text-sm" data-id="element-49">{item.desc}</p>
                </div>
              </div>)}
          </div>

          {/* MIRÁ POR DENTRO - Carousel */}
          <PreviewCarousel data-id="element-50" />
        </div>
      </section>

      {/* TRANSFORMATION */}
      <TransformationSection data-id="element-51" />

      {/* BONUSES */}
      <BonusesSection data-id="element-52" />

      {/* PRICING / CHECKOUT */}
      <section id="comprar" className="py-20 px-4 bg-gradient-to-b from-blue-50 via-blue-100 to-blue-50" data-id="element-53">
        <div className="max-w-4xl mx-auto text-center" data-id="element-54">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900" data-id="element-55">
            Empezá a enseñar con claridad hoy mismo
          </h2>
          <p className="text-xl text-gray-600 mb-10" data-id="element-56">
            Dejá de perder tiempo planificando. Volvé a disfrutar el aula.
          </p>

          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-blue-200 mx-auto max-w-2xl" data-id="element-57">
            <div className="mb-8 flex justify-center" data-id="element-58">
              <img src="https://cdn.magicpatterns.com/uploads/5BpCQQ8Jiju8J6W3NYcLKg/Cover_micro_y_macro.png" alt="Micro y Macro Economía 360" className="w-full max-w-sm drop-shadow-2xl" data-id="element-59" />
            </div>

            <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2" data-id="element-60">
              Precio Final
            </div>
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-61">
              <span className="text-2xl text-gray-400 font-medium line-through" data-id="element-62">
                $49.990
              </span>
              <span className="text-6xl font-extrabold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent" data-id="element-63">
                $19.990
              </span>
            </div>

            <div className="space-y-3 mb-8 text-left" data-id="element-64">
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200" data-id="element-65">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-66" />
                <div data-id="element-67">
                  <span className="font-bold text-gray-900 block" data-id="element-68">
                    Pack Completo Micro y Macro Economía 360
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-69">
                    Clases estructuradas para todo el año lectivo con ejemplos
                    argentinos actuales
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200" data-id="element-70">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-71" />
                <div data-id="element-72">
                  <span className="font-bold text-gray-900 block" data-id="element-73">
                    4 Bonos Exclusivos (Gratis por hoy)
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-74">
                    Banco de ejemplos, clases express, evaluaciones listas y
                    mapa anual de contenidos
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200" data-id="element-75">
                <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-76" />
                <div data-id="element-77">
                  <span className="font-bold text-gray-900 block" data-id="element-78">
                    Acceso Inmediato y Vitalicio
                  </span>
                  <span className="text-sm text-gray-600" data-id="element-79">
                    Descargá todo el material al instante y accedé cuando lo
                    necesites, sin vencimiento
                  </span>
                </div>
              </div>
            </div>

            <a href="https://checkout.rincondigital.shop/cart/52910368882963:1" className="inline-block w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold text-lg py-4 px-8 rounded-full shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 mb-4" data-id="element-80">
              ACCEDÉ AHORA Y EMPEZÁ A ENSEÑAR
            </a>

            <div className="flex items-center justify-center gap-4 text-xs text-gray-500" data-id="element-81">
              <span className="flex items-center gap-1" data-id="element-82">
                <Shield className="w-3 h-3" data-id="element-83" /> Compra Segura
              </span>
              <span className="flex items-center gap-1" data-id="element-84">
                <BookOpen className="w-3 h-3" data-id="element-85" /> Material Digital
              </span>
            </div>
          </div>

          {/* UPSELL 1 */}
          <div className="mt-12 relative max-w-2xl mx-auto" data-id="element-86">
            {/* Hot badge ribbon */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10" data-id="element-87">
              <span className="bg-gradient-to-r from-orange-500 to-amber-500 text-white font-black px-6 py-2 rounded-full text-sm uppercase tracking-widest shadow-lg whitespace-nowrap" data-id="element-88">
                🔥 OFERTA ESPECIAL — SOLO HOY
              </span>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-6 pt-10 md:p-10 md:pt-12 border-2 border-amber-400 shadow-2xl" data-id="element-89">
              <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-1 text-center" data-id="element-90">
                ¿Querés el Pack COMPLETO?
              </h3>
              <p className="text-center text-orange-700 font-semibold mb-6" data-id="element-91">
                Agregá 100 Micro-Emprendimientos Ganadores
              </p>

              <div className="flex justify-center mb-6" data-id="element-92">
                <img src="https://cdn.magicpatterns.com/uploads/xAjgHnUpYaAzxRwbxneGpU/ChatGPT_Image_11_ene_2026,_07_34_47_p.m..png" alt="Micro-Emprendimientos Ganadores" className="w-full max-w-xs drop-shadow-2xl rounded-2xl" data-id="element-93" />
              </div>

              {/* Price block */}
              <div className="bg-white rounded-2xl p-4 mb-6 text-center shadow-inner border border-amber-200" data-id="element-94">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1" data-id="element-95">
                  Precio especial del pack
                </p>
                <div className="flex items-center justify-center gap-3" data-id="element-96">
                  <span className="text-xl text-gray-400 line-through" data-id="element-97">
                    $59.990
                  </span>
                  <span className="text-5xl font-extrabold text-orange-600" data-id="element-98">
                    $26.990
                  </span>
                </div>
                <p className="text-green-600 font-bold text-sm mt-1" data-id="element-99">
                  💎 Ahorrás $33.000
                </p>
              </div>

              <div className="space-y-2 mb-6" data-id="element-100">
                {[{
                text: 'Pack Completo Micro y Macro Economía 360',
                highlight: false
              }, {
                text: '4 Bonos Exclusivos',
                highlight: false
              }, {
                text: '🎁 EXTRA: 100 Micro-Emprendimientos Ganadores',
                highlight: true
              }].map((item, i) => <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${item.highlight ? 'bg-amber-400/20 border-2 border-amber-400 font-bold' : 'bg-white border border-gray-200'}`} data-id="element-101">
                    <Check className={`w-5 h-5 flex-shrink-0 ${item.highlight ? 'text-amber-600' : 'text-green-500'}`} data-id="element-102" />
                    <span className={`text-sm ${item.highlight ? 'text-amber-900' : 'text-gray-800'}`} data-id="element-103">
                      {item.text}
                    </span>
                  </div>)}
              </div>

              <a href="https://checkout.rincondigital.shop/cart/52912857252115:1" className="inline-block w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-extrabold text-lg py-4 px-8 rounded-full shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 text-center" data-id="element-104">
                SÍ, QUIERO EL PACK FULL →
              </a>
            </div>
          </div>

          {/* UPSELL 2 */}
          <div className="mt-12 relative max-w-2xl mx-auto" data-id="element-105">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10" data-id="element-106">
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-black px-6 py-2 rounded-full text-sm uppercase tracking-widest shadow-lg whitespace-nowrap" data-id="element-107">
                ⭐ MEJOR VALOR — PACK COMPLETO
              </span>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-6 pt-10 md:p-10 md:pt-12 border-2 border-blue-500 shadow-2xl" data-id="element-108">
              <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-1 text-center" data-id="element-109">
                Llevate TODO el Sistema 360
              </h3>
              <p className="text-center text-blue-700 font-semibold mb-6" data-id="element-110">
                SIC 360 + Micro y Macro + Emprendimientos
              </p>

              <div className="mb-6 overflow-hidden rounded-2xl shadow-2xl" data-id="element-111">
                <img src="https://cdn.magicpatterns.com/uploads/aUcdZDr96ZbqovVX9TrgaS/Cover_SIC_360.png" alt="Pack Full 360" className="w-full object-cover" data-id="element-112" />
              </div>

              {/* Price block */}
              <div className="bg-white rounded-2xl p-4 mb-6 text-center shadow-inner border border-blue-200" data-id="element-113">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1" data-id="element-114">
                  Precio especial del pack
                </p>
                <div className="flex items-center justify-center gap-3" data-id="element-115">
                  <span className="text-xl text-gray-400 line-through" data-id="element-116">
                    $69.990
                  </span>
                  <span className="text-5xl font-extrabold text-blue-700" data-id="element-117">
                    $39.990
                  </span>
                </div>
                <p className="text-green-600 font-bold text-sm mt-1" data-id="element-118">
                  💰 Ahorrás $30.000
                </p>
              </div>

              <div className="space-y-2 mb-6" data-id="element-119">
                {[{
                text: 'Micro y Macro Economía 360',
                highlight: false
              }, {
                text: '4 Bonos Exclusivos',
                highlight: false
              }, {
                text: '🎁 EXTRA: SIC 360 + 100 Micro-Emprendimientos',
                highlight: true
              }].map((item, i) => <div key={i} className={`flex items-center gap-3 p-3 rounded-xl ${item.highlight ? 'bg-blue-500/15 border-2 border-blue-500 font-bold' : 'bg-white border border-gray-200'}`} data-id="element-120">
                    <Check className={`w-5 h-5 flex-shrink-0 ${item.highlight ? 'text-blue-600' : 'text-green-500'}`} data-id="element-121" />
                    <span className={`text-sm ${item.highlight ? 'text-blue-900' : 'text-gray-800'}`} data-id="element-122">
                      {item.text}
                    </span>
                  </div>)}
              </div>

              <a href="https://checkout.rincondigital.shop/cart/52917897363731:1" className="inline-block w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-extrabold text-lg py-4 px-8 rounded-full shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300 text-center" data-id="element-123">
                SÍ, QUIERO EL PACK FULL 360 →
              </a>
            </div>
          </div>

          {/* ¿Por qué elegir el Pack Full 360? */}
          <div className="mt-16 bg-white rounded-3xl p-8 md:p-10 shadow-xl border-2 border-blue-200 max-w-2xl mx-auto" data-id="element-124">
            <h3 className="text-2xl md:text-3xl font-bold text-center mb-8" data-id="element-125">
              ¿Por qué elegir el{' '}
              <span className="text-orange-600" data-id="element-126">Pack Full 360</span>?
            </h3>
            <div className="space-y-6" data-id="element-127">
              <div className="flex flex-col items-center text-center" data-id="element-128">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-129">
                  <TrendingUp className="w-8 h-8 text-green-600" data-id="element-130" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2" data-id="element-131">
                  Ahorro máximo
                </h4>
                <p className="text-gray-700" data-id="element-132">
                  <span className="text-green-600 font-bold text-2xl" data-id="element-133">
                    $30.000
                  </span>{' '}
                  menos que por separado
                </p>
              </div>
              <div className="flex flex-col items-center text-center" data-id="element-134">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4" data-id="element-135">
                  <BookOpen className="w-8 h-8 text-blue-600" data-id="element-136" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2" data-id="element-137">
                  Cobertura total
                </h4>
                <p className="text-gray-700" data-id="element-138">SIC + Micro y Macro completas</p>
              </div>
              <div className="flex flex-col items-center text-center" data-id="element-139">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4" data-id="element-140">
                  <Gift className="w-8 h-8 text-orange-600" data-id="element-141" />
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2" data-id="element-142">
                  Doble valor
                </h4>
                <p className="text-gray-700" data-id="element-143">8 bonos gratis en total</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GUARANTEE - Compact inline banner */}
      <div className="px-4 py-5 bg-gradient-to-r from-blue-700 to-blue-800" data-id="element-144">
        <div className="max-w-3xl mx-auto flex items-center gap-4 justify-center flex-wrap" data-id="element-145">
          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg flex-shrink-0" data-id="element-146">
            <Shield className="w-6 h-6 text-blue-600" strokeWidth={2.5} data-id="element-147" />
          </div>
          <div className="text-center md:text-left" data-id="element-148">
            <span className="text-white font-extrabold text-lg" data-id="element-149">
              Garantía 7 días sin riesgo
            </span>
            <span className="text-blue-200 text-sm ml-2" data-id="element-150">
              — Si no te convence, te devolvemos el 100% de tu dinero. Sin
              preguntas.
            </span>
          </div>
        </div>
      </div>

      {/* TESTIMONIALS - Compact carousel */}
      <TestimonialsCarousel data-id="element-151" />

      {/* FAQ */}
      <section className="py-16 px-4 max-w-4xl mx-auto bg-gray-50" data-id="element-152">
        <h2 className="text-3xl font-bold text-center text-blue-900 mb-4" data-id="element-153">
          Resolvé aquí tus DUDAS
        </h2>
        <p className="text-center text-gray-600 mb-12" data-id="element-154">
          Las preguntas más frecuentes sobre el pack
        </p>

        <div className="space-y-4" data-id="element-155">
          {[{
          question: '¿Qué recibo al comprar el Kit?',
          answer: 'Recibís el pack completo de Micro y Macro Economía 360 con todas las clases estructuradas para el año lectivo, más los 4 bonos exclusivos: Banco de Ejemplos Reales Argentinos, Clases Express para Días Caóticos, Evaluaciones Listas + Rúbricas, y Mapa Anual de Contenidos.'
        }, {
          question: '¿Cuándo me llega?',
          answer: 'El acceso es inmediato. Apenas confirmás tu compra, recibís un email con el link de descarga y podés empezar a usar el material al instante.'
        }, {
          question: '¿Cómo lo recibo?',
          answer: 'Te llega por email un link de acceso a una plataforma donde podés descargar todo el material en formato digital (PDF). Es simple, rápido y seguro.'
        }, {
          question: '¿Es físico o digital?',
          answer: 'Es 100% digital. Esto te permite acceder desde cualquier dispositivo, descargarlo cuantas veces quieras y tenerlo siempre disponible sin depender de envíos.'
        }, {
          question: '¿Necesito ser profesional?',
          answer: 'No necesitás título específico. El material está diseñado para cualquier docente que dicte economía en nivel secundario, sin importar tu formación de base.'
        }, {
          question: '¿Tengo acceso para siempre?',
          answer: 'Sí, el acceso es vitalicio. Una vez que comprás, el material es tuyo para siempre. Podés descargarlo y usarlo todos los años que quieras, sin vencimiento.'
        }, {
          question: '¿Qué hago si no me llega el acceso?',
          answer: 'Revisá tu carpeta de spam o correo no deseado. Si no lo encontrás, escribinos y te reenviamos el acceso en minutos. Estamos para ayudarte.'
        }, {
          question: '¿Es una compra segura?',
          answer: 'Totalmente segura. Usamos plataformas de pago certificadas que protegen tus datos. Además, tenés garantía de 7 días: si no te convence, te devolvemos el 100% de tu dinero.'
        }].map((faq, index) => <details key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group" data-id="element-156">
              <summary className="flex items-center justify-between p-6 cursor-pointer hover:bg-blue-50 transition-colors" data-id="element-157">
                <span className="font-bold text-gray-900 pr-4" data-id="element-158">
                  {faq.question}
                </span>
                <svg className="w-5 h-5 text-blue-900 transform group-open:rotate-180 transition-transform flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-159">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-160" />
                </svg>
              </summary>
              <div className="px-6 pb-6 text-gray-700 leading-relaxed border-t border-gray-100 pt-4" data-id="element-161">
                {faq.answer}
              </div>
            </details>)}
        </div>

        <div className="mt-12 text-center" data-id="element-162">
          <button onClick={() => document.getElementById('comprar')?.scrollIntoView({
          behavior: 'smooth'
        })} className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg px-12 py-4 rounded-full shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300" data-id="element-163">
            Quiero acceder ahora sin riesgo
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-4" data-id="element-164">
        <div className="max-w-6xl mx-auto text-center" data-id="element-165">
          <p className="text-sm" data-id="element-166">
            © 2026 Micro y Macro Economía 360. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}
// =============================================
// PREVIEW CAROUSEL - "MIRÁ POR DENTRO"
// =============================================
function PreviewCarousel() {
  const images = ['https://cdn.magicpatterns.com/uploads/p5eKrfeJtSocDSYZf3vQPw/Hoja_1_mym.png', 'https://cdn.magicpatterns.com/uploads/5b3hNaYKeutna1y87CCcbX/oja_2_mym.png', 'https://cdn.magicpatterns.com/uploads/fs93BBbrdorehYZdmWmFpH/hoja_3_mym.png', 'https://cdn.magicpatterns.com/uploads/9MUeFWSSXydCbvX3hxVTdL/hoja_4_mym.png'];
  const [current, setCurrent] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const goTo = useCallback((index: number) => {
    setCurrent(Math.max(0, Math.min(index, images.length - 1)));
    setTranslateX(0);
  }, [images.length]);
  const prev = () => goTo(current - 1);
  const next = () => goTo(current + 1);
  const handleDragStart = (clientX: number) => {
    setIsDragging(true);
    setStartX(clientX);
  };
  const handleDragMove = (clientX: number) => {
    if (!isDragging) return;
    setTranslateX(clientX - startX);
  };
  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (translateX > 60 && current > 0) goTo(current - 1);else if (translateX < -60 && current < images.length - 1) goTo(current + 1);else setTranslateX(0);
  };
  return <div className="mb-16" data-id="element-167">
      <div className="text-center mb-8" data-id="element-168">
        <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-bold border border-blue-200 mb-4" data-id="element-169">
          <Eye className="w-4 h-4" data-id="element-170" />
          MIRÁ POR DENTRO
        </span>
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" data-id="element-171">
          Páginas reales del material
        </h3>
        <p className="text-gray-600 text-base md:text-lg" data-id="element-172">
          Proyectos paso a paso, clases listas y actividades prácticas
        </p>
      </div>

      <div className="relative max-w-2xl mx-auto" data-id="element-173">
        <div className="overflow-hidden rounded-2xl bg-gray-100 border border-gray-200 shadow-lg cursor-grab active:cursor-grabbing select-none" onMouseDown={e => handleDragStart(e.clientX)} onMouseMove={e => handleDragMove(e.clientX)} onMouseUp={handleDragEnd} onMouseLeave={handleDragEnd} onTouchStart={e => handleDragStart(e.touches[0].clientX)} onTouchMove={e => handleDragMove(e.touches[0].clientX)} onTouchEnd={handleDragEnd} data-id="element-174">
          <div ref={trackRef} className="flex" style={{
          transform: `translateX(calc(-${current * 100}% + ${translateX}px))`,
          transition: isDragging ? 'none' : 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }} data-id="element-175">
            {images.map((src, i) => <div key={i} className="w-full flex-shrink-0" data-id="element-176">
                <img src={src} alt={`Página ${i + 1} del material`} className="w-full h-auto pointer-events-none" draggable={false} data-id="element-177" />
              </div>)}
          </div>
        </div>

        <button onClick={prev} disabled={current === 0} className={`absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center transition-all ${current === 0 ? 'opacity-30 cursor-not-allowed' : 'opacity-90 hover:opacity-100 hover:scale-110'}`} data-id="element-178">
          <ChevronLeft className="w-5 h-5 text-gray-700" data-id="element-179" />
        </button>
        <button onClick={next} disabled={current === images.length - 1} className={`absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center transition-all ${current === images.length - 1 ? 'opacity-30 cursor-not-allowed' : 'opacity-90 hover:opacity-100 hover:scale-110'}`} data-id="element-180">
          <ChevronRight className="w-5 h-5 text-gray-700" data-id="element-181" />
        </button>

        <div className="flex items-center justify-center gap-2 mt-4" data-id="element-182">
          {images.map((_, i) => <button key={i} onClick={() => goTo(i)} className={`rounded-full transition-all duration-300 ${i === current ? 'w-8 h-3 bg-blue-600' : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'}`} data-id="element-183" />)}
        </div>
        <p className="text-center text-sm text-gray-500 mt-2" data-id="element-184">
          {current + 1} / {images.length}
        </p>
      </div>
    </div>;
}
// =============================================
// BONUSES SECTION - 2x2 Grid
// =============================================
function TestimonialsCarousel() {
  const testimonials = [{
    name: 'María Fernanda López',
    age: 42,
    city: 'Buenos Aires',
    quote: 'Después de 15 años dando economía, este material cambió completamente mi forma de enseñar. Mis alumnos ahora participan y entienden. Ya no improviso más.'
  }, {
    name: 'Roberto Carlos Martínez',
    age: 38,
    city: 'Córdoba',
    quote: 'Lo que más valoro es el tiempo que me ahorra. Llego a casa y puedo estar con mi familia en vez de planificar hasta las 11 de la noche. Las clases están impecables.'
  }, {
    name: 'Silvana Beatriz Rodríguez',
    age: 51,
    city: 'Rosario',
    quote: 'Pensé que ya lo había visto todo en educación. Pero este pack me devolvió las ganas de enseñar. Los ejemplos argentinos son oro puro para conectar con los chicos.'
  }, {
    name: 'Gustavo Hernán Pérez',
    age: 45,
    city: 'Mendoza',
    quote: 'Soy docente hace 20 años y siempre me costó explicar macro. Con este material, hasta los conceptos más complejos se vuelven claros. Mis alumnos aprueban más y mejor.'
  }, {
    name: 'Laura Cecilia Gómez',
    age: 39,
    city: 'La Plata',
    quote: 'Compré este pack con dudas, pero fue la mejor inversión del año. Ahora entro al aula segura, con todo preparado. Mis clases tienen otra calidad.'
  }];
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setCurrent(c => (c + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [paused, testimonials.length]);
  const t = testimonials[current];
  return <section className="py-10 px-4 bg-gray-50" data-id="element-185">
      <div className="max-w-2xl mx-auto" data-id="element-186">
        <h2 className="text-2xl font-bold text-center text-blue-900 mb-6" data-id="element-187">
          Lo que dicen otros docentes
        </h2>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg border-l-4 border-amber-400 p-6 mb-4 cursor-pointer" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onTouchStart={() => setPaused(true)} onTouchEnd={() => setPaused(false)} data-id="element-188">
          <div className="flex gap-1 text-amber-400 mb-3" data-id="element-189">
            {[...Array(5)].map((_, i) => <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20" data-id="element-190">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-191" />
              </svg>)}
          </div>
          <p className="text-gray-700 italic text-base mb-4 leading-relaxed min-h-[72px]" data-id="element-192">
            "{t.quote}"
          </p>
          <div className="flex items-center gap-3" data-id="element-193">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0" data-id="element-194">
              {t.name.charAt(0)}
            </div>
            <div data-id="element-195">
              <p className="font-bold text-blue-900 text-sm" data-id="element-196">
                {t.name}, {t.age} años
              </p>
              <p className="text-xs text-gray-500" data-id="element-197">{t.city}</p>
            </div>
          </div>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2" data-id="element-198">
          {testimonials.map((_, i) => <button key={i} onClick={() => {
          setCurrent(i);
          setPaused(true);
          setTimeout(() => setPaused(false), 6000);
        }} className={`rounded-full transition-all duration-300 ${i === current ? 'w-6 h-2.5 bg-blue-600' : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400'}`} data-id="element-199" />)}
        </div>
      </div>
    </section>;
}
function BonusesSection() {
  const bonuses = [{
    number: 1,
    image: 'https://cdn.magicpatterns.com/uploads/2L7ieLTnGsHwgUeNbMQ2FC/bono_1_mym.png',
    title: 'Banco de Ejemplos Reales Argentinos',
    description: 'La economía explicada con la vida cotidiana: inflación, dólar, supermercado.'
  }, {
    number: 2,
    image: 'https://cdn.magicpatterns.com/uploads/7o1CJFEzhfMRgLYSX6ZJjM/bono_2_mym.png',
    title: 'Clases Express para Días Caóticos',
    description: 'Qué dar cuando no tuviste tiempo de preparar nada. Salvavidas pedagógicos.'
  }, {
    number: 3,
    image: 'https://cdn.magicpatterns.com/uploads/4Tqm3jNt7xdPPnPCf3dpHv/bono_3_mym.png',
    title: 'Evaluaciones Listas + Rúbricas',
    description: 'Evaluar sin perder horas. Pruebas escritas y trabajos prácticos listos.'
  }, {
    number: 4,
    image: 'https://cdn.magicpatterns.com/uploads/kYMFVh7pVZ8SsnQcB3HJ7b/bono_4_mym.png',
    title: 'Mapa Anual de Contenidos',
    description: 'Todo el año de economía en una sola hoja. Secuencia ideal.'
  }];
  const [ref, isInView] = useInView();
  return <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white" data-id="element-200">
      <div className="max-w-3xl mx-auto" data-id="element-201">
        <div className="text-center mb-10" data-id="element-202">
          <span className="inline-flex items-center gap-2 bg-green-600 text-white font-bold px-6 py-3 rounded-full text-sm shadow-lg mb-6" data-id="element-203">
            🎁 BONOS GRATIS HOY
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-204">
            Te llevás{' '}
            <span className="text-green-600 italic" data-id="element-205">4 bonos exclusivos</span>
          </h2>
        </div>

        <div ref={ref} className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6 mb-10" data-id="element-206">
          {bonuses.map((bonus, index) => <div key={bonus.number} className={`bg-white rounded-2xl border border-gray-200 shadow-md overflow-hidden transition-all duration-600 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{
          transitionDelay: `${index * 120}ms`
        }} data-id="element-207">
              <div className="flex items-center justify-between px-3 pt-3" data-id="element-208">
                <span className="bg-gray-900 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-209">
                  BONO {bonus.number}
                </span>
                <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full" data-id="element-210">
                  GRATIS
                </span>
              </div>
              <div className="px-3 py-3" data-id="element-211">
                <img src={bonus.image} alt={bonus.title} className="w-full aspect-square object-cover rounded-xl" data-id="element-212" />
              </div>
              <div className="px-3 pb-4" data-id="element-213">
                <h3 className="font-bold text-gray-900 text-sm sm:text-base leading-tight mb-1" data-id="element-214">
                  {bonus.title}
                </h3>
                <p className="text-gray-500 text-xs sm:text-sm leading-snug" data-id="element-215">
                  {bonus.description}
                </p>
              </div>
            </div>)}
        </div>

        <div className="text-center" data-id="element-216">
          <button onClick={() => document.getElementById('comprar')?.scrollIntoView({
          behavior: 'smooth'
        })} className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg px-12 py-4 rounded-full shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300" data-id="element-217">
            Quiero el pack completo + los bonos
          </button>
        </div>
      </div>
    </section>;
}
// =============================================
// TRANSFORMATION SECTION
// =============================================
function TransformationSection() {
  const pairs = [{
    before: 'Llegabas a clase con dudas',
    after: 'Llegás con todo listo'
  }, {
    before: 'Improvisabas ejemplos',
    after: 'Sabés exactamente qué explicar'
  }, {
    before: 'Veías caras de confusión',
    after: 'Los alumnos entienden y participan'
  }, {
    before: 'Sentías inseguridad',
    after: 'Volvés a sentirte seguro y profesional'
  }];
  const [ref, isInView] = useInView({
    threshold: 0.15
  });
  return <section className="py-16 px-4 bg-white" data-id="element-218">
      <div className="max-w-4xl mx-auto" data-id="element-219">
        {/* Header */}
        <div className="text-center mb-10" data-id="element-220">
          <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest mb-3" data-id="element-221">
            Tu nueva realidad
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900" data-id="element-222">
            Cómo cambia tu día a día
          </h2>
        </div>

        {/* Column headers */}
        <div className="grid grid-cols-2 gap-3 mb-3 max-w-2xl mx-auto" data-id="element-223">
          <div className="flex items-center justify-center gap-2 bg-red-100 text-red-700 font-black text-sm uppercase tracking-widest py-2 rounded-xl" data-id="element-224">
            <span data-id="element-225">😓</span> Antes
          </div>
          <div className="flex items-center justify-center gap-2 bg-green-100 text-green-700 font-black text-sm uppercase tracking-widest py-2 rounded-xl" data-id="element-226">
            <span data-id="element-227">✅</span> Ahora
          </div>
        </div>

        {/* Pairs */}
        <div ref={ref} className="space-y-3 max-w-2xl mx-auto mb-10" data-id="element-228">
          {pairs.map((pair, i) => <div key={i} className={`grid grid-cols-2 gap-3 transition-all duration-500 ease-out ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{
          transitionDelay: `${i * 100}ms`
        }} data-id="element-229">
              {/* Before */}
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-id="element-230">
                <span className="text-red-400 text-lg flex-shrink-0" data-id="element-231">✗</span>
                <span className="text-red-800 text-sm font-medium leading-snug" data-id="element-232">
                  {pair.before}
                </span>
              </div>
              {/* After */}
              <div className="flex items-center gap-3 bg-green-50 border border-green-300 rounded-xl px-4 py-3 shadow-sm" data-id="element-233">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" data-id="element-234" />
                <span className="text-green-900 text-sm font-semibold leading-snug" data-id="element-235">
                  {pair.after}
                </span>
              </div>
            </div>)}
        </div>

        {/* CTA */}
        <div className="text-center" data-id="element-236">
          <button onClick={() => document.getElementById('comprar')?.scrollIntoView({
          behavior: 'smooth'
        })} className="bg-amber-500 hover:bg-amber-600 text-white font-extrabold text-lg px-12 py-4 rounded-full shadow-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300" data-id="element-237">
            Sí, quiero enseñar con claridad →
          </button>
        </div>
      </div>
    </section>;
}